import React, { useState } from 'react';
import { Star } from 'lucide-react';
import { testimonialsAPI } from '../services/api';
import { useAPI } from '../hooks/useAPI';
import { getMockTestimonials } from '../data/mockData';
import ContactModal from './ContactModal';
import { useTranslation } from 'react-i18next';

const Testimonials = () => {
  const { t } = useTranslation();
  const { data: testimonials, loading, error } = useAPI(testimonialsAPI.getAll);
  const mockTestimonials = getMockTestimonials(t);
  const displayTestimonials = testimonials || (error ? mockTestimonials : null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (loading) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="text-xl text-gray-600">{t('testimonials.loading')}</div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-8 sm:py-12 lg:py-16 bg-white">
      <div className="max-w-6xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 uppercase tracking-wide mb-4">
            {t('testimonials.title')}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-sm sm:text-base">
            {t('testimonials.description')}
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          {displayTestimonials?.map((testimonial) => (
            <div key={testimonial.id} className="bg-gray-50 p-6 sm:p-8 rounded-lg border-l-4 border-blue-500 hover:shadow-md transition-shadow">
              {/* Star Rating */}
              <div className="flex items-center mb-4">
                <div className="flex text-yellow-400 mr-3">
                  {[...Array(testimonial.rating || 5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 fill-current" />
                  ))}
                </div>
                <span className="text-sm text-gray-600 font-medium">
                  {testimonial.rating || 5}/5 {t('testimonials.stars')}
                </span>
              </div>

              {/* Testimonial Text */}
              <blockquote className="text-gray-700 text-base sm:text-lg leading-relaxed mb-6 italic">
                "{testimonial.text}"
              </blockquote>

              {/* Customer Info */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="font-bold text-gray-800 text-sm sm:text-base">{testimonial.name}</h4>
                <p className="text-gray-600 text-xs sm:text-sm">{testimonial.role}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-8 sm:mt-12">
          <div className="bg-blue-50 p-6 sm:p-8 rounded-lg">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3">
              {t('testimonials.ctaTitle')}
            </h3>
            <p className="text-gray-600 mb-4 text-sm sm:text-base">
              {t('testimonials.ctaDescription')}
            </p>
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-blue-500 text-white px-6 sm:px-8 py-3 rounded-lg font-medium hover:bg-blue-600 transition-colors text-sm sm:text-base"
            >
              {t('testimonials.ctaButton')}
            </button>
          </div>
        </div>
      </div>
      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </section>
  );
};

export default Testimonials;
